import React, { useState } from 'react';
import { Card } from './ui/card';
import { Button } from './ui/button';
import CopyStrategyModal, { CopyStrategy } from './CopyStrategyModal';
import { useNavigate } from 'react-router-dom';

interface TradingGroup {
  id: number;
  rank: number;
  name: string;
  creator: string;
  trades: number;
  roi: string;
  pnl: string;
  winRate: string;
  scamRate: string;
  usersWinRate: string;
  icon: string;
}

const DiscoverPage: React.FC = () => {
  const navigate = useNavigate();
  const [activeFilter, setActiveFilter] = useState('ai-suggest');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedGroup, setSelectedGroup] = useState<TradingGroup | null>(null);
  const [activeTab, setActiveTab] = useState<'groups' | 'traders'>('groups');
  const [filter, setFilter] = useState<'ai' | 'pnl' | '7d'>('ai');

  const handleCopyStrategy = (group: TradingGroup) => {
    setSelectedGroup(group);
    setIsModalOpen(true);
  };

  const handleSaveStrategy = (strategy: CopyStrategy) => {
    // Save to localStorage for now, later integrate with backend
    const existingStrategies = JSON.parse(localStorage.getItem('copyStrategies') || '[]');
    const updatedStrategies = [...existingStrategies, strategy];
    localStorage.setItem('copyStrategies', JSON.stringify(updatedStrategies));
    
    // Navigate to strategies page
    navigate('/strategies');
  };

  const tradingGroups: TradingGroup[] = [
    {
      id: 1,
      rank: 1,
      name: "Lets 57",
      creator: "MercurySmile",
      trades: 354371,
      roi: "17%",
      pnl: "+$49,367,670.99",
      winRate: "72.43%",
      scamRate: "0.0037%",
      usersWinRate: "25%",
      icon: "😊"
    },
    {
      id: 2,
      rank: 2,
      name: "Lets2 72",
      creator: "MercurySmile",
      trades: 400374,
      roi: "15%",
      pnl: "+$44,213,687.87",
      winRate: "67.99%",
      scamRate: "0.0005%",
      usersWinRate: "17%",
      icon: "😊"
    },
    {
      id: 3,
      rank: 3,
      name: "AVNT_winners 20",
      creator: "willpowered.eth",
      trades: 256977,
      roi: "24%",
      pnl: "+$28,889,936.08",
      winRate: "75.61%",
      scamRate: "0%",
      usersWinRate: "0%",
      icon: "🎯"
    },
    {
      id: 4,
      rank: 4,
      name: "top 45",
      creator: "BlueEarthworm",
      trades: 93279,
      roi: "14%",
      pnl: "+$22,418,103.23",
      winRate: "74.8%",
      scamRate: "0.0043%",
      usersWinRate: "17%",
      icon: "🏆"
    },
    {
      id: 5,
      rank: 5,
      name: "Diamond Hands",
      creator: "CryptoWhale.eth",
      trades: 187654,
      roi: "32%",
      pnl: "+$18,765,432.10",
      winRate: "68.45%",
      scamRate: "0.0012%",
      usersWinRate: "42%",
      icon: "💎"
    },
    {
      id: 6,
      rank: 6,
      name: "Moon Mission",
      creator: "ToTheMoon",
      trades: 298765,
      roi: "28%",
      pnl: "+$16,543,210.87",
      winRate: "71.23%",
      scamRate: "0.0021%",
      usersWinRate: "33%",
      icon: "🚀"
    },
    {
      id: 7,
      rank: 7,
      name: "Alpha Squad",
      creator: "AlphaTrader",
      trades: 145678,
      roi: "26%",
      pnl: "+$14,876,543.21",
      winRate: "69.87%",
      scamRate: "0.0008%",
      usersWinRate: "38%",
      icon: "⚡"
    },
    {
      id: 8,
      rank: 8,
      name: "DeFi Masters",
      creator: "DeFiKing",
      trades: 234567,
      roi: "22%",
      pnl: "+$12,345,678.90",
      winRate: "66.54%",
      scamRate: "0.0034%",
      usersWinRate: "29%",
      icon: "🏦"
    },
    {
      id: 9,
      rank: 9,
      name: "NFT Legends",
      creator: "NFTCrafter",
      trades: 98765,
      roi: "35%",
      pnl: "+$9,876,543.21",
      winRate: "73.21%",
      scamRate: "0.0015%",
      usersWinRate: "45%",
      icon: "🎨"
    },
    {
      id: 10,
      rank: 10,
      name: "Yield Farmers",
      creator: "YieldMax",
      trades: 176543,
      roi: "19%",
      pnl: "+$8,765,432.10",
      winRate: "65.43%",
      scamRate: "0.0027%",
      usersWinRate: "31%",
      icon: "🌾"
    },
    {
      id: 11,
      rank: 11,
      name: "HODL Warriors",
      creator: "HODLMaster",
      trades: 123456,
      roi: "31%",
      pnl: "+$7,654,321.09",
      winRate: "70.12%",
      scamRate: "0.0009%",
      usersWinRate: "41%",
      icon: "🛡️"
    },
    {
      id: 12,
      rank: 12,
      name: "Bull Market",
      creator: "BullRider",
      trades: 287654,
      roi: "16%",
      pnl: "+$6,543,210.98",
      winRate: "64.78%",
      scamRate: "0.0041%",
      usersWinRate: "27%",
      icon: "🐂"
    },
    {
      id: 13,
      rank: 13,
      name: "Smart Money",
      creator: "SmartTrader",
      trades: 156789,
      roi: "25%",
      pnl: "+$5,432,109.87",
      winRate: "67.89%",
      scamRate: "0.0018%",
      usersWinRate: "36%",
      icon: "🧠"
    },
    {
      id: 14,
      rank: 14,
      name: "Crypto Kings",
      creator: "KingMaker",
      trades: 198765,
      roi: "21%",
      pnl: "+$4,321,098.76",
      winRate: "63.45%",
      scamRate: "0.0032%",
      usersWinRate: "28%",
      icon: "👑"
    },
    {
      id: 15,
      rank: 15,
      name: "Risk Takers",
      creator: "RiskMaster",
      trades: 87654,
      roi: "38%",
      pnl: "+$3,210,987.65",
      winRate: "58.76%",
      scamRate: "0.0054%",
      usersWinRate: "22%",
      icon: "🎲"
    }
  ];

  const getFilterButtonClass = (filterType: string) => {
    return filter === filterType
      ? 'bg-blue-500/20 text-blue-400 border border-blue-500/30'
      : 'bg-transparent text-green-400/70 hover:bg-green-500/10 hover:text-green-400';
  };

  const getTabButtonClass = (tabType: string) => {
    return activeTab === tabType
      ? 'bg-green-500/20 text-green-400 border border-green-500/30'
      : 'bg-transparent text-green-400/70 hover:bg-green-500/10 hover:text-green-400';
  };

  return (
    <div className="min-h-full bg-[#0a0a1a] text-green-400 font-mono p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-green-400 mb-2">Discover</h1>
          <div className="text-green-400/70 text-sm">
            Jumpstart your trading with user-curated groups
          </div>
        </div>
      </div>

      {/* Main Content - Full Width */}
      <div className="w-full">
        {/* Tabs */}
        <div className="flex space-x-1 mb-6">
          <Button
            onClick={() => setActiveTab('groups')}
            className={`px-4 py-2 text-sm rounded transition-colors ${getTabButtonClass('groups')}`}
          >
            Top Groups
          </Button>
          <Button
            onClick={() => setActiveTab('traders')}
            className={`px-4 py-2 text-sm rounded transition-colors ${getTabButtonClass('traders')}`}
          >
            Top Traders
          </Button>
        </div>

        {/* Title and Description */}
        <div className="mb-6">
          <h2 className="text-3xl font-bold text-green-400 mb-4">
            Top Groups in The Last 7 Days
          </h2>
          <p className="text-green-400/70 text-sm leading-relaxed max-w-4xl">
            Jumpstart your trading with user-curated groups. COPYCAT automates the trades - but outcomes depend on your strategy and market conditions.
          </p>
        </div>

        {/* Filters */}
        <div className="flex space-x-2 mb-6">
            <Button
              onClick={() => setFilter('ai')}
              className={`px-4 py-2 text-sm rounded transition-colors ${getFilterButtonClass('ai')}`}
            >
              ✨ AI Suggest
            </Button>
            <Button
              onClick={() => setFilter('pnl')}
              className={`px-4 py-2 text-sm rounded transition-colors ${getFilterButtonClass('pnl')}`}
            >
              📊 Top PnL
            </Button>
            <Button
              onClick={() => setFilter('7d')}
              className={`px-4 py-2 text-sm rounded transition-colors ${getFilterButtonClass('7d')}`}
            >
              🕐 7D
            </Button>
          </div>

          {/* Groups Table */}
          <div className="bg-gradient-to-br from-[#0d0d2a]/80 to-[#0a0a1a]/90 border border-green-500/20 rounded-xl overflow-hidden backdrop-blur-sm shadow-2xl">
            <div className="overflow-x-auto">
              <table className="w-full table-fixed">
                <colgroup>
                  <col className="w-[8%]" />
                  <col className="w-[22%]" />
                  <col className="w-[12%]" />
                  <col className="w-[8%]" />
                  <col className="w-[15%]" />
                  <col className="w-[10%]" />
                  <col className="w-[10%]" />
                  <col className="w-[10%]" />
                  <col className="w-[5%]" />
                </colgroup>
                <thead>
                  <tr className="bg-gradient-to-r from-green-500/15 to-green-400/10 border-b border-green-500/20">
                    <th className="text-left px-3 py-4 text-green-300 text-xs font-semibold uppercase tracking-wider">
                      Rank
                    </th>
                    <th className="text-left px-3 py-4 text-green-300 text-xs font-semibold uppercase tracking-wider">
                      Group
                    </th>
                    <th className="text-left px-3 py-4 text-green-300 text-xs font-semibold uppercase tracking-wider">
                      Trades
                    </th>
                    <th className="text-left px-3 py-4 text-green-300 text-xs font-semibold uppercase tracking-wider">
                      ROI
                    </th>
                    <th className="text-left px-3 py-4 text-green-300 text-xs font-semibold uppercase tracking-wider">
                      PnL
                    </th>
                    <th className="text-left px-3 py-4 text-green-300 text-xs font-semibold uppercase tracking-wider">
                      Win Rate
                    </th>
                    <th className="text-left px-3 py-4 text-green-300 text-xs font-semibold uppercase tracking-wider">
                      Scam Rate
                    </th>
                    <th className="text-left px-3 py-4 text-green-300 text-xs font-semibold uppercase tracking-wider">
                      Users Win Rate
                    </th>
                    <th className="text-center px-3 py-4 text-green-300 text-xs font-semibold uppercase tracking-wider">
                      Copy
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-green-500/10">
                  {tradingGroups.map((group, index) => (
                    <tr 
                      key={group.id} 
                      className={`
                        hover:bg-gradient-to-r hover:from-green-500/5 hover:to-transparent 
                        transition-all duration-200 group
                        ${index < 3 ? 'bg-gradient-to-r from-yellow-500/5 to-transparent' : ''}
                      `}
                    >
                      <td className="px-3 py-5">
                        <div className="flex items-center justify-center">
                          <div className={`
                            w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold
                            ${index === 0 ? 'bg-gradient-to-r from-yellow-400 to-yellow-600 text-black' : 
                              index === 1 ? 'bg-gradient-to-r from-gray-300 to-gray-500 text-black' :
                              index === 2 ? 'bg-gradient-to-r from-orange-400 to-orange-600 text-black' :
                              'bg-green-500/20 text-green-400 border border-green-500/30'}
                          `}>
                            {group.rank}
                          </div>
                        </div>
                      </td>
                      <td className="px-3 py-5">
                        <div className="flex items-center space-x-2">
                          <div className="w-7 h-7 rounded-full bg-gradient-to-br from-green-400/20 to-green-600/20 flex items-center justify-center text-sm border border-green-500/30 flex-shrink-0">
                            {group.icon}
                          </div>
                          <div className="min-w-0 flex-1">
                            <div className="text-green-100 font-semibold text-sm group-hover:text-green-300 transition-colors truncate">
                              {group.name}
                            </div>
                            <div className="text-green-400/60 text-xs mt-0.5 truncate">
                              by {group.creator}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-3 py-5">
                        <div className="text-green-400 font-mono text-sm">
                          {group.trades.toLocaleString()}
                        </div>
                      </td>
                      <td className="px-3 py-5">
                        <div className={`
                          font-semibold text-xs px-2 py-1 rounded-md inline-block
                          ${parseFloat(group.roi) > 0 ? 'text-green-300 bg-green-500/10' : 'text-red-300 bg-red-500/10'}
                        `}>
                          {group.roi}
                        </div>
                      </td>
                      <td className="px-3 py-5">
                        <div className={`
                          font-bold text-sm truncate
                          ${group.pnl.startsWith('+') ? 'text-green-300' : 'text-red-300'}
                        `}>
                          {group.pnl}
                        </div>
                      </td>
                      <td className="px-3 py-5">
                        <div className="flex items-center space-x-1">
                          <div className={`
                            w-2 h-2 rounded-full flex-shrink-0
                            ${parseFloat(group.winRate) > 70 ? 'bg-green-400' : 
                              parseFloat(group.winRate) > 50 ? 'bg-yellow-400' : 'bg-red-400'}
                          `}></div>
                          <span className="text-green-400 text-sm font-medium">
                            {group.winRate}
                          </span>
                        </div>
                      </td>
                      <td className="px-3 py-5">
                        <div className={`
                          text-xs font-medium px-2 py-1 rounded-md inline-block
                          ${parseFloat(group.scamRate) < 1 ? 'text-green-300 bg-green-500/10' : 'text-red-300 bg-red-500/10'}
                        `}>
                          {group.scamRate}
                        </div>
                      </td>
                      <td className="px-3 py-5">
                        <div className="text-green-400 text-sm font-medium">
                          {group.usersWinRate}
                        </div>
                      </td>
                      <td className="px-3 py-5">
                        <div className="flex items-center justify-center">
                          <Button 
                            size="sm" 
                            onClick={() => handleCopyStrategy(group)}
                            className="
                              bg-gradient-to-r from-green-500/20 to-green-600/20 
                              hover:from-green-500/30 hover:to-green-600/30 
                              text-green-300 border border-green-500/40 
                              text-xs font-semibold px-2 py-1 rounded-md
                              transition-all duration-200 hover:scale-105
                              hover:shadow-lg hover:shadow-green-500/20
                            "
                          >
                            Copy
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            </div>
      </div>
 {/* Closes the w-full div from line 267 */}

{/* Copy Strategy Modal */}
<CopyStrategyModal
  isOpen={isModalOpen}
  onClose={() => setIsModalOpen(false)}
  onSave={handleSaveStrategy}
  groupName={selectedGroup?.name}
/>
</div>
);
};

export default DiscoverPage;